import { getRuntimeConfig } from './runtime-config'

interface Environment {
  readonly apiBaseUrl: string
  readonly apiDefaultTimeoutMs: number
  readonly isDevelopment: boolean
  readonly isProduction: boolean
  readonly msalClientId: string
  readonly msalTenantId: string
  readonly msalApiScope: string
  readonly apimBaseUrl: string
  /**
   * Local-development-only toggle for the "Framework" sidebar item (`VITE_SHOW_FRAMEWORK`).
   * Gated behind `import.meta.env.DEV` so it is always `false` in any built bundle — dev,
   * staging, and production — regardless of how the env var is set; `import.meta.env.DEV` is
   * statically replaced with `false` by Vite at build time and the branch is dead-code
   * eliminated, so the flag cannot reach a deployed environment.
   */
  readonly showFramework: boolean
}

let cachedEnvironment: Environment | null = null

// `import.meta.env.DEV` is statically inlined by Vite, so this branch (and `VITE_SHOW_FRAMEWORK`)
// is dead-code eliminated from any built bundle — the flag can only ever be `true` when running
// the local Vite dev server.
const showFramework = import.meta.env.DEV && import.meta.env.VITE_SHOW_FRAMEWORK === 'true'

const getEnvironment = (): Environment => {
  const runtimeConfig = getRuntimeConfig()

  // If we have runtime config, ALWAYS recreate the cache with it
  if (runtimeConfig) {
    cachedEnvironment = {
      // API Configuration
      // In development: direct to APIM via Vite proxy
      // In production: SWA handles routing to the backend
      apiBaseUrl:
        (import.meta.env.VITE_API_BASE_URL as string) || (import.meta.env.DEV ? '/api' : '/api'),
      apiDefaultTimeoutMs: Number(import.meta.env.VITE_API_TIMEOUT_MS ?? 30000),

      // Environment
      isDevelopment: import.meta.env.DEV,
      isProduction: import.meta.env.PROD,

      // MSAL Configuration - Runtime loaded
      msalClientId: runtimeConfig.msalClientId,
      msalTenantId: runtimeConfig.msalTenantId,
      msalApiScope: runtimeConfig.msalApiScope,

      // APIM Configuration - Runtime loaded
      apimBaseUrl: runtimeConfig.apimBaseUrl,

      showFramework,
    }
    return cachedEnvironment
  }

  // Only use cache if in dev mode and no runtime config
  if (cachedEnvironment && import.meta.env.DEV) {
    return cachedEnvironment
  }

  // Fallback to build-time env vars (only valid for local development)
  if (import.meta.env.DEV) {
    console.warn('[Environment] Using build-time environment variables from .env file')
  }

  cachedEnvironment = {
    apiBaseUrl:
      (import.meta.env.VITE_API_BASE_URL as string) || (import.meta.env.DEV ? '/api' : '/api'),
    apiDefaultTimeoutMs: Number(import.meta.env.VITE_API_TIMEOUT_MS ?? 30000),
    isDevelopment: import.meta.env.DEV,
    isProduction: import.meta.env.PROD,
    msalClientId: (import.meta.env.VITE_MSAL_CLIENT_ID as string) || '',
    msalTenantId: (import.meta.env.VITE_MSAL_TENANT_ID as string) || '',
    msalApiScope: (import.meta.env.VITE_MSAL_API_SCOPE as string) || '',
    apimBaseUrl: (import.meta.env.VITE_APIM_BASE_URL as string) || '',
    showFramework,
  }
  return cachedEnvironment
}

// Export as a getter to ensure it's evaluated lazily
export const environment = new Proxy({} as Environment, {
  get(_, prop) {
    return getEnvironment()[prop as keyof Environment]
  },
})
